import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { DashboardCountersProvider } from '../../../shared/contexts/DashboardCountersContext';
import { useNavigate } from 'react-router-dom';
import { sitesV2Api, SiteV2 } from '../../../api/sitesV2Api';
import { siteMembersApi, SiteMember } from '../../../api/siteMembersApi';
import { UniversalHeader } from '../../../shared/ui';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import { SiteShiftsCard } from '../../siteShifts';
import { AccountingCard } from '../../accounting';
import { ExecutorBalancesCard, type ExecutorBalancesCardHandle } from '../../executorBalances';
import { QuestionsV2Card } from '../../questionsV2';
import { NotesCard } from '../../notes';
import { SupplyCard } from '../../supply';
import { ActiveShiftsV2Card } from '../../activeShifts';
import { MyShiftCardV2 } from '../../myShiftV2';
import { PaymentsV2Card } from '../../paymentsV2';
import { IncomingPaymentsV2Card } from '../../incomingPaymentsV2';
import { TestCard } from '../../test';
import { WorkTypesSummaryCard } from '../../workTypesSummary';
import { EstimatesCard } from '../../estimates';
import { ContractsCard } from '../../contracts';
import StationsCard from '../../stations/ui/StationsCard';
import DevicesCard from '../../stations/ui/DevicesCard';
import SiteInfoPage from './SiteInfoPage';
import ObjectPageTabs, { ObjectPageTab } from './ObjectPageTabs';
import ObjectWallTab from './ObjectWallTab';
import MyFinancesCard from './MyFinancesCard';
import {
  FULLSCREEN_CARD_OPENED_EVENT,
  FULLSCREEN_CARD_CLOSED_EVENT,
  FULLSCREEN_CARD_CLOSING_BY_BACK_EVENT,
} from '../../../shared/ui/cards/FullscreenCardModal';
import config from '../../../config/config';
import styles from './ObjectPage.module.css';

interface ObjectPageProps {
  objectId: number;
  onBack?: () => void;
  initialSite?: SiteV2; // Начальный объект из списка (если есть, показываем сразу)
}

const ObjectPage: React.FC<ObjectPageProps> = ({ objectId, onBack, initialSite }) => {
  const navigate = useNavigate();
  const { showBack, hideBack } = useNavigation();
  const [object, setObject] = useState<SiteV2 | null>(initialSite || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSiteInfoPage, setShowSiteInfoPage] = useState(false);
  const [members, setMembers] = useState<SiteMember[]>([]);
  const [activeTab, setActiveTab] = useState<ObjectPageTab>('main');
  const pageRef = useRef<HTMLDivElement>(null);
  const executorBalancesRef = useRef<ExecutorBalancesCardHandle>(null);
  const isNavigatingRef = useRef(false); // Защита от двойного вызова навигации
  // Игнорировать следующий «Назад» сразу после закрытия fullscreen-карточки (защита от двойного срабатывания)
  const ignoreBackUntilRef = useRef(0);
  // Не перезаписывать кнопку «Назад», пока открыта fullscreen-карточка (иначе карточка думает, что закрываем окно объекта)
  const fullscreenCardOpenRef = useRef(false);

  // Обработчик кнопки "Назад" (объявляем ДО использования в useEffect)
  const handleBack = useCallback(() => {
    if (isNavigatingRef.current) return;
    if (Date.now() < ignoreBackUntilRef.current) return;

    isNavigatingRef.current = true;

    if (onBack) {
      onBack();
    } else {
      navigate('/dashboard-new');
    }

    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 1000);
  }, [onBack, navigate]);

  const loadObject = useCallback(async () => {
    try {
      setError(null);
      // Не поднимаем «Загрузка...» в шапке, если объект уже пришёл из списка — иначе затираем спокойный первый кадр
      const hasInitialFromList = initialSite != null && initialSite.id === objectId;
      if (!hasInitialFromList) {
        setLoading(true);
      }

      const [siteData, membersData] = await Promise.all([
        sitesV2Api.getSite(objectId),
        siteMembersApi.getActiveSiteMembers(objectId),
      ]);

      setObject(siteData);
      setMembers(membersData);
    } catch (err) {
      setError('Ошибка загрузки объекта');
      console.error('Error loading object:', err);
    } finally {
      setLoading(false);
    }
  }, [objectId, initialSite]);

  useEffect(() => {
    // Если начальный объект передан, показываем его сразу (мгновенный показ)
    if (initialSite && initialSite.id === objectId) {
      setObject(initialSite);
      setLoading(false); // Не показываем загрузку, если объект уже есть
    }

    loadObject();
  }, [objectId, loadObject]);

  // Отслеживаем, открыта ли fullscreen-карточка — чтобы не перезаписывать её обработчик «Назад»
  useEffect(() => {
    const onOpened = () => { fullscreenCardOpenRef.current = true; };
    const onClosed = () => { fullscreenCardOpenRef.current = false; };
    window.addEventListener(FULLSCREEN_CARD_OPENED_EVENT, onOpened);
    window.addEventListener(FULLSCREEN_CARD_CLOSED_EVENT, onClosed);
    return () => {
      window.removeEventListener(FULLSCREEN_CARD_OPENED_EVENT, onOpened);
      window.removeEventListener(FULLSCREEN_CARD_CLOSED_EVENT, onClosed);
    };
  }, []);

  // Вешаем «Назад» = переход в список. Не трогаем, если открыта fullscreen-карточка (у неё свой слой в контексте).
  useEffect(() => {
    if (showSiteInfoPage) return;
    if (fullscreenCardOpenRef.current) return;
    showBack(handleBack);
  }, [showBack, handleBack, showSiteInfoPage]);

  // При размонтировании ObjectPage (закрытие страницы объекта) обязательно скрываем стрелку «Назад».
  useEffect(() => {
    return () => hideBack();
  }, [hideBack]);

  const prevShowSiteInfoPageRef = useRef(showSiteInfoPage);
  const closeSiteInfoPage = useCallback(() => setShowSiteInfoPage(false), []);
  useEffect(() => {
    if (showSiteInfoPage) {
      showBack(closeSiteInfoPage);
    } else if (prevShowSiteInfoPageRef.current) {
      showBack(handleBack);
    }
    prevShowSiteInfoPageRef.current = showSiteInfoPage;
  }, [showSiteInfoPage, showBack, handleBack, closeSiteInfoPage]);

  // Коoldown при закрытии карточки кнопкой «Назад», чтобы второй срабатывание (визуальная/системная) не уводило в список.
  useEffect(() => {
    const onClosingByBack = () => {
      ignoreBackUntilRef.current = Date.now() + 500;
    };
    window.addEventListener(FULLSCREEN_CARD_CLOSING_BY_BACK_EVENT, onClosingByBack);
    return () => window.removeEventListener(FULLSCREEN_CARD_CLOSING_BY_BACK_EVENT, onClosingByBack);
  }, []);

  // После закрытия фул-карточки восстанавливаем обработчик; коoldown уже выставлен через FULLSCREEN_CARD_CLOSING_BY_BACK_EVENT.
  useEffect(() => {
    const onFullscreenCardClosed = () => {
      isNavigatingRef.current = false;
      ignoreBackUntilRef.current = Math.max(ignoreBackUntilRef.current, Date.now() + 400);
      setTimeout(() => {
        if (!showSiteInfoPage) {
          showBack(handleBack);
        }
      }, 100);
    };
    window.addEventListener(FULLSCREEN_CARD_CLOSED_EVENT, onFullscreenCardClosed);
    return () => window.removeEventListener(FULLSCREEN_CARD_CLOSED_EVENT, onFullscreenCardClosed);
  }, [showBack, handleBack, showSiteInfoPage]);

  // Обработка клавиши Escape для выхода
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleBack();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleBack]);

  const handleSiteUpdated = () => {
    sitesV2Api.invalidateSiteCache(objectId);
    loadObject();
  };
  const handleMembersClick = () => {
    setShowSiteInfoPage(true);
  };

  // Функция для получения правильного названия участников с окончаниями
  const getMembersLabel = (count: number) => {
    // Для всех типов контента используем "участник"
    if (count === 1) {
      return 'участник';
    } else if (count >= 2 && count <= 4) {
      return 'участника';
    } else {
      return 'участников';
    }
  };

  const getAvatarUrl = (): string | undefined => {
    if (!object?.avatarUrl || !object.avatarUrl.trim()) return undefined;
    const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
    return object.avatarUrl.startsWith('http') ? object.avatarUrl : `${apiBaseUrl}${object.avatarUrl}`;
  };

  const avatarUrl = object ? getAvatarUrl() : undefined;

  // Карточки «Главная» и «Управление» — ленивая загрузка внутри каждой карточки
  const mainCards = useMemo(() => (
    <>
      <MyFinancesCard siteId={objectId} />
      <MyShiftCardV2 siteId={objectId} />
      <ActiveShiftsV2Card siteId={objectId} />
      <div className={styles.contentCardsRow}>
        <QuestionsV2Card siteId={objectId} />
        <NotesCard siteId={objectId} />
        <SupplyCard siteId={objectId} />
      </div>
      <SiteShiftsCard siteId={objectId} myOnly />
    </>
  ), [objectId]);

  const managementCards = useMemo(() => (
    <>
      <SiteShiftsCard siteId={objectId} />
      <AccountingCard
        siteId={objectId}
        onOpenExecutorBalance={(siteExecutorId) => {
          executorBalancesRef.current?.openWithExecutor(siteExecutorId);
        }}
      />
      <ExecutorBalancesCard ref={executorBalancesRef} siteId={objectId} listHidden />
      <PaymentsV2Card siteId={objectId} />
      <IncomingPaymentsV2Card siteId={objectId} />
      <WorkTypesSummaryCard siteId={objectId} />
      <StationsCard siteId={objectId} />
      <DevicesCard siteId={objectId} />
      <TestCard siteId={objectId} />
      <EstimatesCard siteId={objectId} />
      <ContractsCard siteId={objectId} />
    </>
  ), [objectId]);

  return (
    <div 
      ref={pageRef}
      className={styles.objectPage}
    >
      <div className={styles.objectPageScroll}>
      {error && (
        <div style={{ 
          padding: '16px', 
          margin: '16px', 
          backgroundColor: 'var(--color-danger-light)', 
          color: 'var(--color-danger)', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      <DashboardCountersProvider siteId={objectId}>
        <ObjectPageTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          mainContent={mainCards}
          feedContent={<ObjectWallTab siteId={objectId} />}
          managementContent={managementCards}
          renderLayout={({ tabBar, panels }) => (
            <>
              <div className={styles.objectPageStickyChrome}>
                <UniversalHeader
                  className="object-page-header"
                  suppressBackRegistration={true}
                  title={
                    loading && !object
                      ? 'Загрузка...'
                      : (object?.name || 'Объект')
                  }
                  onBack={handleBack}
                  onClick={object ? handleMembersClick : undefined}
                  clickable={!!object}
                  centered={true}
                  avatar={object ? {
                      icon: object.closed ? 'ri-archive-line' : 'ri-building-2-line',
                      imageUrl: avatarUrl,
                      color: object.closed ? 'var(--theme-text-muted)' : 'var(--object-page-avatar-bg, #ffffff)'
                    } : undefined}
                  infoRow={object ? {
                    members: {
                      count: members.length,
                      onClick: handleMembersClick,
                      getLabel: getMembersLabel
                    },
                    ...(object.closed ? {
                      status: {
                        text: 'Закрыт',
                        type: 'closed' as const
                      }
                    } : {})
                  } : undefined}
                />
                {tabBar}
              </div>
              <div className={styles.objectPageTabPanels}>{panels}</div>
            </>
          )}
        />
      </DashboardCountersProvider>
      </div>

      {object && (
        <SiteInfoPage
          isOpen={showSiteInfoPage}
          site={object}
          siteId={objectId}
          onClose={() => setShowSiteInfoPage(false)}
          onSiteUpdated={handleSiteUpdated}
          initialMembers={members}
        />
      )}
    </div>
  );
};

export default ObjectPage;

